import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../app/store";
import { DraftFMR1Fields, selectFMR1DraftFmrFields, updateFMR1DraftField } from "../../../logic/frm1Slice";
import FmrTwoColumnTable, { FmrTwoColumnTableRowProps } from "../common/fmrTwoColumnTable";
import { FraudClass, FraudDetectionMethod, FraudTypes, LEACurrentStatus, StaffAccountabilityInvestigationStatus } from "../../../../../api/fmr1";

const FRAUD_TYPE_OPTIONS = [
  ...Object.values(FraudTypes).map((value) => ({
    name: value,
    value,
  })),
];

export const PART1_TABLE4_ROWS: Array<FmrTwoColumnTableRowProps<DraftFMR1Fields>> = [
  {
    label: "Table 4: FMR",
    kind: "section",
  },
  {
    label: "Particulars",
    rightSectionLabel: "Amount",
    kind: "section",
  },
  {
    label: "6.a Classification of Fraud",
    key: "fraud_classification",
    type: "dropdown",
    disabled: true,
    options: [
      {
        name: "Misappropriation of funds and criminal breach of trust;",
        value: FraudClass.MISAPPROPRIATION_AND_BREACH_OF_TRUST,
      },
      {
        name: "Fraudulent encashment through forged instruments;",
        value: FraudClass.FRAUDULENT_ENCASHMENT_FORGED_INSTRUMENTS,
      },
      {
        name: "Manipulation of books of accounts or through fictitious accounts, and conversion of property;",
        value: FraudClass.MANIPULATION_OF_ACCOUNTS_AND_PROPERTY,
      },
      {
        name: "Cheating by concealment of facts with the intention to deceive any person and cheating by impersonation;",
        value: FraudClass.CHEATING_AND_IMPERSONATION,
      },
      {
        name: "Forgery with the intention to commit fraud by making any false documents/electronic records;",
        value: FraudClass.FORGERY_TO_COMMIT_FRAUD,
      },
      {
        name: "Wilful falsification, destruction, alteration, mutilations of any book, electronic record, paper, writing, valuable security or account with intent to defraud;",
        value: FraudClass.WILFUL_DESTRUCTION_OR_ALTERATION_OF_RECORDS,
      },
      {
        name: "Fraudulent credit facilities extended for illegal gratification;",
        value: FraudClass.FRAUDULENT_CREDIT_FOR_ILLEGAL_GRATIFICATION,
      },
      {
        name: "Cash shortages on account of frauds;",
        value: FraudClass.CASH_SHORTAGES_DUE_TO_FRAUD,
      },
      {
        name: "Fraudulent transactions involving foreign exchange;",
        value: FraudClass.FOREIGN_EXCHANGE_FRAUD,
      },
      {
        name: "Fraudulent electronic banking/ digital payment related transactions;",
        value: FraudClass.DIGITAL_BANKING_FRAUD,
      },
      {
        name: "Other type of fraudulent activity not covered under any of the above (Please specify);",
        value: FraudClass.OTHERS,
      },
    ],
  },
  {
    label: "6.a.1 Others (Specify)",
    key: "fraud_classification_other",
    type: "text",
  },
  {
    label: "6.b Type of fraud",
    key: "fraud_type",
    type: "dropdown",
    options: FRAUD_TYPE_OPTIONS,
  },
  {
    label: "6.b.1 Others (need to be specified)",
    key: "fraud_type_other",
    type: "text",
  },
  {
    label:
      "6.c Whether there was any compromise in the IT systems including software, website, switches, mobile apps, UPI, etc.? **",
    key: "is_it_compromised",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "6.d If yes, details **",
    key: "it_compromise_details",
    type: "text",
    alignTop: true,
  },
  {
    label: "7.a Total amount involved **",
    key: "amount_involved",
    type: "number",
  },
  {
    label: "7.b Amount frozen in the account with the reporting bank **",
    key: "frozen_amount",
    type: "number",
  },
  {
    label: "8.a Date of occurrence **",
    key: "fraud_occurrence_date",
    type: "date",
  },
  {
    label: "8.b Date of detection of fraud **",
    key: "fraud_detection_date",
    type: "date",
  },
  {
    label: "8.c Date of classification as fraud**",
    key: "fraud_classification_date",
    type: "date",
  },
  {
    label: "8.d Date on which reported to RBI **",
    key: "fraud_reported_to_rbi_date",
    type: "date",
  },
  {
    label: "8.e Reasons for delay, if any, in reporting the fraud to RBI **",
    key: "fraud_reporting_delay_reason",
    type: "text",
    alignTop: true,
  },
  {
    label:
      "8.f Date of full payment of the defrauded amount/ Date of settlement amount agreed upon in case of compromised settlement",
    key: "fraud_settlement_date",
    type: "date",
  },

  // 9 Detected / Audit details
  {
    label: "9.a How the fraud was detected? **",
    key: "detection_method",
    type: "dropdown",
    options: [
      {
        name: "Customer complaints",
        value: FraudDetectionMethod.CUSTOMER_COMPLAINTS,
      },
      {
        name: "Internal Inspections/Audits",
        value: FraudDetectionMethod.INTERNAL_INSPECTIONS_AUDITS,
      },
      { name: "External Audit", value: FraudDetectionMethod.EXTERNAL_AUDIT },
      { name: "MIS of bank", value: FraudDetectionMethod.BANK_MIS },
      {
        name: "News & Media (including social media)",
        value: FraudDetectionMethod.NEWS_AND_MEDIA,
      },
      { name: "CFR/CRILC", value: FraudDetectionMethod.CFR_CRILC },
      { name: "Whistleblower", value: FraudDetectionMethod.WHISTLEBLOWER },
      {
        name: "Intimation/notice from Law Enforcement Agencies/RBI",
        value: FraudDetectionMethod.LAW_ENFORCEMENT_OR_RBI_INTIMATION,
      },
      {
        name: "Intimation from other banks",
        value: FraudDetectionMethod.INTIMATION_FROM_OTHER_BANKS,
      },
      { name: "EWS Triggering", value: FraudDetectionMethod.EWS_TRIGGERING },
      {
        name: "Other sources (Specify)",
        value: FraudDetectionMethod.OTHER_SOURCES,
      },
    ],
  },
  {
    label: "9.a.1 Other sources (Specify)",
    key: "detection_other_source",
    type: "text",
  },
  {
    label: "9.b Date of Commission of Internal/External Audit",
    key: "internal_external_audit_commission_date",
    type: "date",
  },
  {
    label: "9.c Date of Internal/External Audit",
    key: "internal_external_audit_date",
    type: "date",
  },
  {
    label: "9.d Name of External Auditor",
    key: "external_auditor_name",
    type: "text",
  },
  {
    label: "9.e Amount diverted as per Internal/External Audit Report",
    key: "diverted_amount",
    type: "number",
  },
  {
    label: "9.f Brief History including Root Cause analysis **",
    key: "root_cause_history",
    type: "text",
    alignTop: true,
  },
  {
    label: "9.g Modus operandi **",
    key: "modus_operandi",
    type: "text",
    alignTop: true,
  },

  // 10 Fraud committed by
  { label: "10 Fraud committed by", kind: "section" },
  {
    label: "10.a Staff **",
    key: "perpetrators_staff_involvement",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "10.b Customers **",
    key: "perpetrators_customers_involvement",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "10.c Outsiders **",
    key: "perpetrators_outsiders_involvement",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },

  // 11
  {
    label:
      "11.a Whether the controlling office (Regional/Zonal) could detect the fraud by a scrutiny of control returns submitted by the branch **",
    key: "is_fraud_detectable_via_control_return_scrutiny",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "11.b Whether there is need to improve the information system? **",
    key: "is_information_system_improvement_needed",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },

  // 12
  {
    label:
      "12.a Whether internal inspection/ audit (including concurrent audit) was conducted at the branch(es) during the period between the date of first occurrence of the fraud and its detection? **",
    key: "is_inspection_conducted",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "12.b If yes, why the fraud could not have been detected during such inspection/audit. **",
    key: "reason_for_non_detection",
    type: "text",
    alignTop: true,
  },
  {
    label: "12.c What action has been taken for non-detection of the fraud during such inspection/audit **",
    key: "inspection_audit_action_taken",
    type: "text",
    alignTop: true,
  },

  // 13 Actions
  { label: "13 Action taken/proposed to be taken", kind: "section" },
  {
    label: "13.a Complaint with Law Enforcement Agency (LEA)",
    kind: "section",
  },
  {
    label: "13.a (i) Whether any complaint has been lodged with the LEA? **",
    key: "is_lea_complaint_lodged",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "13.a (ii) If yes, name of office/ branch of LEA",
    key: "lea_office_name",
    type: "text",
  },
  {
    label: "13.a (ii) 1 Date of reference **",
    key: "lea_complaint_reference_date",
    type: "date",
  },
  {
    label: "13.a (ii) 2 Present position of the case **",
    key: "lea_complaint_present_position",
    type: "dropdown",
    options: [
      {
        name: "FIR/Police Complaint registered",
        value: LEACurrentStatus.FIR_REGISTERED,
      },
      {
        name: "Under investigation by CBI/Police",
        value: LEACurrentStatus.UNDER_INVESTIGATION,
      },
      { name: "Charge-sheeted", value: LEACurrentStatus.CHARGE_SHEETED },
      {
        name: "Court Case under process",
        value: LEACurrentStatus.COURT_CASE_UNDER_PROCESS,
      },
      {
        name: "Verdict given - acquitted",
        value: LEACurrentStatus.VERDICT_ACQUITTED,
      },
      {
        name: "Verdict given - convicted",
        value: LEACurrentStatus.VERDICT_CONVICTED,
      },
    ],
  },
  {
    label: "13.a (ii) 3 Date of completion of LEA investigation",
    key: "lea_complaint_investigation_completion_date",
    type: "date",
  },
  {
    label: "13.a (ii) 4 Date of submission of investigation report by LEA",
    key: "lea_complaint_report_submission_date",
    type: "date",
  },
  {
    label: "13.a (ii) 5 Date of final order/verdict by the court",
    key: "lea_complaint_court_order_date",
    type: "date",
  },
  {
    label: "13.a (ii) 6 Details of final order/verdict by the court",
    key: "lea_complaint_court_order_details",
    type: "text",
    alignTop: true,
  },
  {
    label: "13.a (iii) If not reported to LEA, reasons thereof",
    key: "lea_complaint_not_reported_reason",
    type: "text",
    alignTop: true,
  },

  { label: "13.b Recovery suit with DRT/Court/NCLT", kind: "section" },
  {
    label: "13.b (i) Date of filing",
    key: "recovery_suit_filing_date",
    type: "date",
  },
  {
    label: "13.b (ii) Present position",
    key: "recovery_suit_present_position",
    type: "text",
    alignTop: true,
  },

  { label: "13.c Insurance claim", kind: "section" },
  {
    label: "13.c (i) Whether any claim has been lodged with an insurance company? **",
    key: "is_insurance_claim_lodged",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "13.c (ii) If not, reasons thereof. **",
    key: "insurance_claim_reasons_not_lodged",
    type: "text",
    alignTop: true,
  },

  { label: "13.d Details of staff-side action", kind: "section" },
  {
    label: "13.d (i) Whether any internal investigation has been conducted? **",
    key: "is_internal_investigation_conducted",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "13.d (ii) If yes, date of completion **",
    key: "internal_investigation_completion_date",
    type: "date",
  },
  {
    label: "13.d (iii) Whether any departmental enquiry has been conducted? **",
    key: "is_departmental_enquiry_conducted",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "13.d (v) Present Status **",
    key: "staff_side_action_present_status",
    type: "dropdown",
    options: [
      {
        name: "Examination Under Process",
        value: StaffAccountabilityInvestigationStatus.EXAMINATION_UNDER_PROCESS,
      },
      {
        name: "No Staff accountability",
        value: StaffAccountabilityInvestigationStatus.NO_STAFF_ACCOUNTABILITY,
      },
      {
        name: "Examination completed and staff details given in Sheet 2",
        value: StaffAccountabilityInvestigationStatus.EXAMINATION_COMPLETED_DETAILS_PROVIDED,
      },
    ],
  },

  {
    label: "13.e Steps taken/proposed to be taken to avoid such incidents **",
    key: "avoidance_measures",
    type: "text",
    alignTop: true,
  },

  // 14 Recovery/Provisions
  {
    label: "14.a Amount Recovered",
    key: "total_amount_recovered",
    type: "number",
  },
  {
    label: "14.a (i) Amount recovered from party/parties concerned **",
    key: "amounts_recovered_from_parties",
    type: "number",
  },
  {
    label: "14.a (ii) From insurance **",
    key: "amounts_recovered_from_insurance",
    type: "number",
  },
  {
    label: "14.a (iii) From other sources **",
    key: "amounts_recovered_other_sources",
    type: "number",
  },

  {
    label: "14.b Extent of loss to the bank **",
    key: "loss_to_bank",
    type: "number",
  },
  { label: "14.c Provision held **", key: "provision_held", type: "number" },
  {
    label: "14.d Amount written off **",
    key: "written_off_amount",
    type: "number",
  },

  {
    label: "14.e(i) Whether compromise settlement done ** (Yes/No)",
    key: "is_compromise_settlement_done",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  {
    label: "14.e(ii) Compromise settled amount**",
    key: "settlement_amount",
    type: "number",
  },

  {
    label: "15 Suggestions for consideration of RBI **",
    key: "suggestions_for_rbi",
    type: "text",
    alignTop: true,
  },
  {
    label: "16 Other developments**",
    key: "other_developments_details",
    type: "text",
    alignTop: true,
  },

  {
    label: "17.a Whether case is closed **",
    key: "is_case_closed",
    type: "dropdown",
    options: [
      { name: "Yes", value: "Yes" },
      { name: "No", value: "No" },
    ],
  },
  { label: "17.b Date of closure", key: "case_closure_date", type: "date" },
];

export interface FMR1Table4FormProps {
  leftWidthPx?: number;
}

const FMR1Table4Form: React.FC<FMR1Table4FormProps> = ({ leftWidthPx }) => {
  const { id: case_id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const draftFields = useAppSelector(selectFMR1DraftFmrFields);

  const handleLocalChange = useCallback((key: keyof DraftFMR1Fields, value: any) => {
    if (!case_id) return;
    dispatch(updateFMR1DraftField({ key: key as keyof DraftFMR1Fields, value }));
  }, [case_id, dispatch]);

  return (
    <>
      <FmrTwoColumnTable<DraftFMR1Fields>
        data={draftFields}
        onChange={handleLocalChange}
        rows={PART1_TABLE4_ROWS}
        leftWidthPx={leftWidthPx ?? 435}
        rowHeightPx={44}
        className="overflow-visible"
      />
    </>
  );
};

FMR1Table4Form.displayName = "FMR1Table4Form";

export default FMR1Table4Form;
